import type { Client } from "openapi-fetch";
import type { paths } from "@comandero/client";

/**
 * Interfaz unificada para el cliente de la API (Agnóstica).
 */
export type ApiClient = Client<paths>;

export interface SignInOptions {
  email: string;
  password: string;
}

export interface SignUpOptions {
  name: string;
  email: string;
  password: string;
}

/**
 * Agnostic API wrapper for shared logic using OpenAPI client.
 */
export const sharedApi = {
  async signIn(api: ApiClient, options: SignInOptions) {
    return api.POST("/api/auth/sign-in/email", {
      body: {
        email: options.email,
        password: options.password,
      },
    });
  },

  async signUp(api: ApiClient, options: SignUpOptions) {
    return api.POST("/api/auth/sign-up/email", {
      body: {
        name: options.name,
        email: options.email,
        password: options.password,
      },
    });
  },

  async signOut(api: ApiClient) {
    return api.POST("/api/auth/sign-out", {}).catch(() => null);
  },

  async getSession(api: ApiClient) {
    return api.GET("/api/auth/get-session", {});
  },

  async listSessions(api: ApiClient) {
    return api.GET("/api/auth/list-sessions", {});
  },

  async revokeSession(api: ApiClient, sessionId: string) {
    return api.POST("/api/auth/revoke-session", {
      body: { id: sessionId },
    });
  },

  async forgotPassword(api: ApiClient, options: { email: string }) {
    return api.POST("/api/auth/forgot-password", {
      body: { email: options.email },
    });
  },

  /** Cambia la contraseña (exige la actual; cierra las demás sesiones). */
  async changePassword(
    api: ApiClient,
    options: { current_password: string; new_password: string },
  ) {
    return api.POST("/api/auth/change-password", {
      body: { current_password: options.current_password, new_password: options.new_password },
    });
  },

  /** Verifica el email con el token recibido por correo. */
  async verifyEmail(api: ApiClient, options: { token: string }) {
    return api.POST("/api/auth/verify-email", {
      body: { token: options.token },
    });
  },

  /** Reenvía el email de verificación al usuario autenticado. */
  async sendVerificationEmail(api: ApiClient) {
    return api.POST("/api/auth/send-verification-email", {});
  },

  /** Programa la eliminación de la cuenta (periodo de gracia; cierra todas las sesiones). */
  async requestAccountDeletion(api: ApiClient) {
    return api.POST("/api/auth/delete-account", {});
  },

  /** Cancela una eliminación de cuenta pendiente (recuperación). */
  async restoreAccount(api: ApiClient) {
    return api.POST("/api/auth/restore-account", {});
  },

  /** Estado de eliminación de la cuenta (fechas de solicitud y purga, o null). */
  async getDeletionStatus(api: ApiClient) {
    return api.GET("/api/auth/deletion-status", {});
  },

  /** Estado de habilitación de los providers sociales (Google/Apple). */
  async socialProviders(api: ApiClient) {
    return api.GET("/api/auth/social/providers", {});
  },

  /** Métodos de acceso vinculados del usuario autenticado. */
  async loginMethods(api: ApiClient) {
    return api.GET("/api/auth/login-methods", {});
  },

  /** Desvincula un provider social (con salvaguarda del último método en el back). */
  async unlinkAccount(api: ApiClient, provider: string) {
    return api.POST("/api/auth/unlink-account", { body: { provider } });
  },
};
